import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { fmtEuro, fmtDate, isoDate } from "@/lib/format";
import { DatePicker } from "@/components/DatePicker";
import { Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const firstOfMonth = () => { const d = new Date(); return isoDate(new Date(d.getFullYear(), d.getMonth(), 1)); };

const Filter = ({ label, children }) => <div><Label className="micro-label">{label}</Label><div className="mt-1.5">{children}</div></div>;

const Pick = ({ value, onChange, options, all, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="bg-white" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent><SelectItem value="all">{all}</SelectItem>{options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
  </Select>
);

export const BillingExport = () => {
  const { parties, projects, companyId } = useData();
  const [f, setF] = useState({ customer_id: "all", project_id: "all", stage: "BILLABLE", date_from: firstOfMonth(), date_to: isoDate(new Date()) });
  const [data, setData] = useState(null);
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const params = useCallback(() => ({ ...f, customer_id: f.customer_id === "all" ? undefined : f.customer_id, project_id: f.project_id === "all" ? undefined : f.project_id,
    company_id: companyId || undefined }), [f, companyId]);
  useEffect(() => { api.get("/production/billing", { params: params() }).then((r) => setData(r.data)).catch(() => setData(null)); }, [params]);

  const download = async (format) => {
    try {
      const r = await api.get("/production/billing", { params: { ...params(), format }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fatturazione_${f.date_from}_${f.date_to}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const customers = parties.filter((p) => p.roles.includes("customer"));
  const projs = projects.filter((p) => f.customer_id === "all" || p.customer_id === f.customer_id).map((p) => ({ id: p.id, name: `${p.code} · ${p.name}` }));

  return (
    <div data-testid="billing-export">
      <div className="mb-4 grid gap-3 border bg-white p-4 md:grid-cols-5">
        <Filter label="Committente"><Pick value={f.customer_id} onChange={(v) => setF({ ...f, customer_id: v, project_id: "all" })} options={customers} all="Tutti i committenti" testId="billing-customer-filter" /></Filter>
        <Filter label="Commessa"><Pick value={f.project_id} onChange={set("project_id")} options={projs} all="Tutte le commesse" testId="billing-project-filter" /></Filter>
        <Filter label="Dal"><DatePicker value={f.date_from} onChange={set("date_from")} testId="billing-date-from" /></Filter>
        <Filter label="Al"><DatePicker value={f.date_to} onChange={set("date_to")} testId="billing-date-to" /></Filter>
        <Filter label="Produzione">
          <Select value={f.stage} onValueChange={set("stage")}>
            <SelectTrigger className="bg-white" data-testid="billing-stage-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="BILLABLE">Solo contabilizzabile</SelectItem><SelectItem value="VALIDATED">Solo validata</SelectItem><SelectItem value="ALL">Validata + contabilizzabile</SelectItem></SelectContent>
          </Select>
        </Filter>
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="border bg-white px-4 py-3"><div className="micro-label">Totale periodo</div><div className="mt-1 font-mono text-2xl font-semibold" data-testid="billing-total">{fmtEuro(data?.totals.amount)}</div></div>
        <div className="border bg-white px-4 py-3"><div className="micro-label">Ordini</div><div className="mt-1 font-mono text-2xl font-semibold" data-testid="billing-orders">{data?.totals.orders ?? 0}</div></div>
        <div className="ml-auto flex gap-2">
          <Button onClick={() => download("xlsx")} disabled={!data?.lines.length} data-testid="billing-download-xlsx"><FileSpreadsheet className="mr-2 h-4 w-4" />Scarica Excel</Button>
          <Button variant="outline" onClick={() => download("csv")} disabled={!data?.lines.length} data-testid="billing-download-csv"><Download className="mr-2 h-4 w-4" />CSV</Button>
        </div>
      </div>
      {!data?.lines.length ? <Empty text="Nessuna produzione nel periodo selezionato" testId="billing-empty" /> : (
        <div className="overflow-x-auto border bg-white" data-testid="billing-summary-table">
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Committente</TableHead><TableHead>Commessa</TableHead><TableHead>Società titolare</TableHead><TableHead className="text-right">Ordini</TableHead><TableHead className="text-right">Importo</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.summary.map((s) => (
                <TableRow key={`${s.customer}${s.project}${s.contract_company}`}>
                  <TableCell className="text-sm font-semibold">{s.customer}</TableCell><TableCell className="text-sm">{s.project}</TableCell>
                  <TableCell className="text-sm">{s.contract_company}</TableCell><TableCell className="text-right font-mono">{s.orders}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{fmtEuro(s.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <p className="mt-3 text-xs text-muted-foreground">Periodo calcolato sulla data di ammissione a valorizzazione (o di validazione). Dal {fmtDate(f.date_from)} al {fmtDate(f.date_to)}. Ogni export viene registrato nel registro modifiche.</p>
    </div>
  );
};
